import React, { useContext, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Box, Flex } from 'rebass';
import { string, func, array } from 'prop-types';
import { CSVLink } from 'react-csv';
import { ButtonStyled, TextStyled } from '@nike/nike-design-system-components';

import { TableServerSideContext } from '~COMPONENTS/tableServerSide/TableServerSideContext';
import TableServerSide from '../../../components/tableServerSide/TableServerSide';

import ActionItemList from '../../common/ActionItemList';

import { updateProductContractGroup } from '../actions';
import { fetchAllLinkedEntities } from './actions';
import { showToast } from '../../../components/toast/actions';

import { getLinkedContractsColumns } from './linkedContractsColumns';

import useModal from '../../../hooks/useModal';

const LinkedContracts = ({ productContractGroupId, linkedContractIds, updateProductContractGroup, showToast }) => {
  const {
    totalResources,
    refreshTableData,
    handleClearSortAndFilters,
    tableInstance: { selectedRowIds = {}, selectedFlatRows, isAllRowsSelected },
    sortQueryParams,
    filterQueryParams
  } = useContext(TableServerSideContext);

  const memoizedColumns = useMemo(() => getLinkedContractsColumns(), []);

  const [isActionItemModalOpen, openActionItemModal, closeActionItemModal] = useModal();

  const csvLinkRef = useRef(null);

  const [allContracts, setAllContracts] = useState([]);

  const selectedContractIds = selectedFlatRows?.map(row => row?.original?.sourceContractId);

  const clickCSVLink = async () => setTimeout(() => csvLinkRef?.current?.link.click(), 1000);

  const handleFetchAllContracts = async () => {
    const filterQueryParamsWithId = `&filter=contractGroupId(${productContractGroupId})${filterQueryParams}`;

    const fetchedContracts = await fetchAllLinkedEntities({
      entity: 'contracts',
      totalResources,
      sortQueryParams,
      filterQueryParams: filterQueryParamsWithId
    });

    return setAllContracts(fetchedContracts);
  };

  const actionItems = {
    Download: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Download',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                if (isAllRowsSelected) {
                  toggleIsLoading(true, 'Downloading CSV...');
                  await handleFetchAllContracts(false);
                  toggleIsLoading(false);
                }

                clickCSVLink();
                closeActionItemModal();
              }
          }
        ],
        content: () => 'Do you want to proceed with downloading the selected contract/s?',
        title: 'Confirm Download'
      }
    },
    '|': { action: () => {} },
    Delete: {
      modal: {
        actions: [
          { actionName: 'Cancel', action: closeActionItemModal },
          {
            actionName: 'Delete',
            action:
              ({ toggleIsLoading }) =>
              async () => {
                toggleIsLoading(true, 'Deleting contract...');

                await updateProductContractGroup({
                  productContractGroupId,
                  data: { contracts: linkedContractIds.filter(id => !selectedContractIds.includes(id)) }
                });

                toggleIsLoading(false);
                refreshTableData();
                closeActionItemModal();

                showToast({ message: 'Contract deleted', type: 'success' }, 5500);
              }
          }
        ],
        content: () => 'Do you want to proceed with deleting the selected contract/s from this product contract group?',
        title: 'Confirm Deletion'
      }
    }
  };

  const csvLinkData = (isAllRowsSelected ? allContracts : selectedFlatRows?.map(row => row.original))?.map(
    ({ sourceContractId, poNumber, beginDate, endDate }) => {
      const expDate = new Date(new Date(endDate).getTime() - 10 * 24 * 60 * 60 * 1000);

      return { sourceContractId, poNumber, beginDate, endDate, exp: new Date() > expDate };
    }
  );

  return (
    <Box mt='15px'>
      <Flex alignItems='center'>
        <TextStyled appearance='body2' style={{ marginLeft: '5px', marginRight: '10px' }}>
          {`${totalResources}`} results
        </TextStyled>

        <ButtonStyled
          appearance='text'
          background='light'
          ariaLabel='Clear All Filters'
          type='button'
          onClick={handleClearSortAndFilters}
          style={{ marginRight: '10px' }}
        >
          Clear All Filters
        </ButtonStyled>

        <ActionItemList
          selected={selectedRowIds}
          actionItems={actionItems}
          isModalOpen={isActionItemModalOpen}
          openModal={openActionItemModal}
          closeModal={closeActionItemModal}
        />

        <CSVLink data={csvLinkData || []} filename='LinkedContracts.csv' ref={csvLinkRef} target='_blank' />
      </Flex>
      <TableServerSide
        columns={memoizedColumns}
        url={`v1/contracts?filter=contractGroupId(${productContractGroupId})`}
        includeRowSelect
        fullWidth
        fullHeight
      />
    </Box>
  );
};

LinkedContracts.propTypes = {
  productContractGroupId: string.isRequired,
  linkedContractIds: array.isRequired,
  updateProductContractGroup: func.isRequired,
  showToast: func.isRequired
};

export default connect(null, { updateProductContractGroup, showToast })(LinkedContracts);
